import React, { useEffect, useRef, useState } from 'react';
import { useBackdropClose } from 'hooks/useBackdropClose';
import ReactDOM from 'react-dom';
import styles from './Controls.module.css';
import { ChipBar, ChipOption } from './Tags';

/* ── 공용 인라인 아이콘 (이모지 금지) ── */

const IcBack = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <polyline points="15 18 9 12 15 6" />
    </svg>
);
const IcClose = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.2" strokeLinecap="round" aria-hidden>
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);
const IcSearch = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" aria-hidden>
        <circle cx="11" cy="11" r="7" /><line x1="20" y1="20" x2="16.7" y2="16.7" />
    </svg>
);
const IcSort = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M7 4v16M7 20l-3-3M7 20l3-3M17 20V4M17 4l-3 3M17 4l3 3" />
    </svg>
);

/* ══════════════════════════════════════════════════════════════════
 * TopBar
 * ════════════════════════════════════════════════════════════════ */

interface TopBarProps {
    title?: string;
    /** 뒤로가기 — 없으면 좌측 자리만 비웁니다 */
    onBack?: () => void;
    /** 우측 닫기 */
    onClose?: () => void;
    /** 우측 커스텀 액션 (닫기 대신) */
    right?: React.ReactNode;
    /** 구분선 없이 배경에 녹임 */
    plain?: boolean;
    /** 접근성 라벨 — i18n 으로 넘깁니다 */
    backLabel?: string;
    closeLabel?: string;
}

export const TopBar = ({
    title, onBack, onClose, right, plain, backLabel, closeLabel,
}: TopBarProps) => (
    <header className={plain ? `${styles.topbar} ${styles.topbarPlain}` : styles.topbar}>
        {onBack ? (
            <button type="button" className={styles.iconBtn} onClick={onBack} aria-label={backLabel}>
                <IcBack />
            </button>
        ) : (
            <span className={`${styles.iconBtn} ${styles.iconBtnGhost}`} aria-hidden />
        )}
        <h1 className={styles.topbarTitle}>{title}</h1>
        {right || (onClose ? (
            <button type="button" className={styles.iconBtn} onClick={onClose} aria-label={closeLabel}>
                <IcClose />
            </button>
        ) : (
            <span className={`${styles.iconBtn} ${styles.iconBtnGhost}`} aria-hidden />
        ))}
    </header>
);

/* ══════════════════════════════════════════════════════════════════
 * Tabs — 상단 분할 탭. 활성 표시는 밑줄(형태)로 합니다.
 * ════════════════════════════════════════════════════════════════ */

export interface TabItem {
    value: string;
    label: string;
    count?: number;
}

interface TabsProps {
    items: TabItem[];
    value: string;
    onChange: (value: string) => void;
    ariaLabel: string;
}

export const Tabs = ({ items, value, onChange, ariaLabel }: TabsProps) => (
    <div className={styles.tabs} role="tablist" aria-label={ariaLabel}>
        {items.map((it) => (
            <button
                key={it.value}
                type="button"
                role="tab"
                aria-selected={it.value === value}
                className={it.value === value ? `${styles.tab} ${styles.tabOn}` : styles.tab}
                onClick={() => onChange(it.value)}
            >
                {it.label}
                {typeof it.count === 'number' && <span className={styles.tabCount}>{it.count}</span>}
            </button>
        ))}
    </div>
);

/* ══════════════════════════════════════════════════════════════════
 * FilterBar — 칩 + 정렬
 * ════════════════════════════════════════════════════════════════ */

interface FilterBarProps {
    options: ChipOption[];
    value: string;
    onChange: (v: string) => void;
    ariaLabel: string;
    /** 현재 정렬 라벨 (예: "가까운 순") */
    sortLabel?: string;
    onSort?: () => void;
}

export const FilterBar = ({
    options, value, onChange, ariaLabel, sortLabel, onSort,
}: FilterBarProps) => (
    <div className={styles.filterBar}>
        <div className={styles.filterChips}>
            <ChipBar options={options} value={value} onChange={onChange} ariaLabel={ariaLabel} />
        </div>
        {sortLabel && onSort ? (
            <button type="button" className={styles.sort} onClick={onSort}>
                {sortLabel}
                <IcSort />
            </button>
        ) : null}
    </div>
);

/* ══════════════════════════════════════════════════════════════════
 * SearchInput
 * ════════════════════════════════════════════════════════════════ */

interface SearchInputProps {
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    onSubmit?: () => void;
    clearLabel?: string;
}

export const SearchInput = ({
    value, onChange, placeholder, onSubmit, clearLabel,
}: SearchInputProps) => {
    const [focused, setFocused] = useState(false);
    return (
        <div className={focused ? `${styles.search} ${styles.searchFocused}` : styles.search}>
            <span className={styles.searchIcon}><IcSearch /></span>
            <input
                className={styles.searchField}
                value={value}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onKeyDown={(e) => { if (e.key === 'Enter') onSubmit?.(); }}
            />
            {value ? (
                <button
                    type="button"
                    className={styles.searchClear}
                    onClick={() => onChange('')}
                    aria-label={clearLabel}
                >
                    <IcClose />
                </button>
            ) : null}
        </div>
    );
};

/* ══════════════════════════════════════════════════════════════════
 * BottomGnb — 하단 고정 탭
 * ════════════════════════════════════════════════════════════════ */

export interface GnbItem {
    value: string;
    label: string;
    icon: (active: boolean) => React.ReactNode;
    /** 알림 점 표시 */
    dot?: boolean;
}

interface BottomGnbProps {
    items: GnbItem[];
    value: string;
    onChange: (v: string) => void;
}

export const BottomGnb = ({ items, value, onChange }: BottomGnbProps) => (
    <nav className={styles.gnb}>
        {items.map((it) => {
            const on = it.value === value;
            return (
                <button
                    key={it.value}
                    type="button"
                    aria-current={on ? 'page' : undefined}
                    className={on ? `${styles.gnbItem} ${styles.gnbOn}` : styles.gnbItem}
                    onClick={() => onChange(it.value)}
                >
                    {it.icon(on)}
                    {it.label}
                    {it.dot ? <i className={styles.gnbDot} aria-hidden /> : null}
                </button>
            );
        })}
    </nav>
);

/* ══════════════════════════════════════════════════════════════════
 * Sheet — 바텀시트. #portal 에 렌더합니다 (기존 index.html 에 존재).
 * ════════════════════════════════════════════════════════════════ */

interface SheetProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    children: React.ReactNode;
    closeLabel?: string;
}

export const Sheet = ({ open, onClose, title, children, closeLabel }: SheetProps) => {
    const backdrop = useBackdropClose<HTMLDivElement>(onClose);
    const overlayRef = backdrop.ref;

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prev;
        };
    }, [open, onClose]);

    if (!open) return null;

    const node = (
        <div
            className={styles.sheetOverlay}
            {...backdrop}
        >
            <div className={styles.sheet} role="dialog" aria-modal="true" aria-label={title}>
                <div className={styles.sheetHandle} aria-hidden />
                {title ? (
                    <div className={styles.sheetHead}>
                        <h2 className={styles.sheetTitle}>{title}</h2>
                        <button
                            type="button"
                            className={styles.iconBtn}
                            onClick={onClose}
                            aria-label={closeLabel}
                        >
                            <IcClose />
                        </button>
                    </div>
                ) : null}
                <div className={styles.sheetBody}>{children}</div>
            </div>
        </div>
    );

    const portal = document.getElementById('portal');
    return portal ? ReactDOM.createPortal(node, portal) : node;
};
